import {
  negateDecimalNumber,
  parseDecimalNumber,
  sumDecimalFractions,
  sumDecimalNumbers,
} from "@/common/lib/beancount/decimal-number";
import { fractionDigitsOf } from "@/common/lib/format/format-number";
import { formatLedgerDate } from "@/common/lib/format/ledger-date";
import { parseConcreteTimeFilter } from "@/features/reports/export/reporting-period";
import {
  balanceToAmounts,
  chooseDisplayUnit,
  collectUnits,
  toExactAmount,
  toFiniteAmount,
} from "./unit-amounts";

export type DataSeries = Array<{
  date: string;
  balance: Record<string, unknown>;
}>;

export type IntervalDataSeries = Array<{
  date: string;
  balance: Record<string, unknown>;
  accountBalances: Record<string, unknown>;
}>;

export type HierarchyNode = {
  account: string;
  balance?: Record<string, unknown> | null;
  children?: HierarchyNode[];
};

export type CurrencyAmount = {
  currency: string;
  value: number;
  /**
   * The decimal the ledger sent, when there is one. Text and differences use
   * it: a double rounds a large balance (9007199254740993) before either sees
   * it (w4/m52). `value` stays for sorting and chart coordinates.
   */
  exact?: string;
};

export type AccountBalanceRow = {
  account: string;
  label: string;
  kind: "asset" | "liability";
  amounts: CurrencyAmount[];
  sortValue: number;
};

export type MovementCategory = {
  account: string;
  label: string;
  amounts: CurrencyAmount[];
  sortValue: number;
};

export type MovementSnapshot = {
  date: string;
  total: CurrencyAmount[];
  average: CurrencyAmount[];
  categories: MovementCategory[];
};

type OverviewData = {
  netWorthData?: DataSeries | null;
  assetsData?: DataSeries | null;
  liabilitiesData?: DataSeries | null;
  incomeIntervalData?: IntervalDataSeries | null;
  incomeData?: DataSeries | null;
  expensesIntervalData?: IntervalDataSeries | null;
  expensesData?: DataSeries | null;
  assetsHierarchyData?: unknown;
  liabilitiesHierarchyData?: unknown;
  incomeHierarchyData?: unknown;
  expensesHierarchyData?: unknown;
};

function hasNonZeroAmount(value: unknown): boolean {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  return Object.values(value).some((amount) => {
    const numeric = toFiniteAmount(amount);
    return numeric !== null && numeric !== 0;
  });
}

/** A zero total can still contain offsetting activity in individual accounts. */
function pointHasActivity(
  point: DataSeries[number] | IntervalDataSeries[number],
): boolean {
  return (
    hasNonZeroAmount(point.balance) ||
    ("accountBalances" in point &&
      Object.values(point.accountBalances ?? {}).some(hasNonZeroAmount))
  );
}

function hierarchyHasActivity(value: unknown): boolean {
  if (Array.isArray(value)) return value.some(hierarchyHasActivity);
  if (!value || typeof value !== "object") return false;

  const node = value as Record<string, unknown>;
  return (
    node.hasTxns === true ||
    hasNonZeroAmount(node.balance) ||
    hasNonZeroAmount(node.balanceChildren) ||
    hasNonZeroAmount(node.cost) ||
    hasNonZeroAmount(node.costChildren) ||
    hierarchyHasActivity(node.children)
  );
}

/**
 * Reports whether an overview contains financial activity worth charting.
 * Empty ledgers can still return zero-filled points and account-tree roots, so
 * array length alone is not sufficient for deciding whether to show charts.
 */
export function hasOverviewActivity(
  overview: OverviewData | null | undefined,
): boolean {
  if (!overview) return false;

  const series = [
    overview.netWorthData,
    overview.assetsData,
    overview.liabilitiesData,
    overview.incomeIntervalData,
    overview.incomeData,
    overview.expensesIntervalData,
    overview.expensesData,
  ];
  const hasSeriesActivity = series.some((points) =>
    points?.some(pointHasActivity),
  );

  return (
    hasSeriesActivity ||
    [
      overview.assetsHierarchyData,
      overview.liabilitiesHierarchyData,
      overview.incomeHierarchyData,
      overview.expensesHierarchyData,
    ].some(hierarchyHasActivity)
  );
}

export function getBalanceAmounts(
  balance: Record<string, unknown> | null | undefined,
  inverted = false,
): CurrencyAmount[] {
  if (!balance) return [];

  return Object.entries(balance)
    .map(([currency, value]): CurrencyAmount | null => {
      const numeric = toFiniteAmount(value);
      if (numeric === null) return null;
      const exact = toExactAmount(value);
      return {
        currency,
        value: inverted ? -numeric : numeric,
        ...(exact !== null && {
          exact: inverted ? negateDecimalNumber(exact) : exact,
        }),
      };
    })
    .filter((amount): amount is CurrencyAmount => amount !== null)
    .sort((a, b) => a.currency.localeCompare(b.currency));
}

const decimalOf = (amount: CurrencyAmount) =>
  amount.exact ?? parseDecimalNumber(String(amount.value)) ?? "0";

/**
 * `latest - previous` for two amounts in one currency, as an exact decimal
 * when both carry the ledger's (w4/m52).
 */
export function amountDifference(
  latest: CurrencyAmount,
  previous: CurrencyAmount,
): string {
  return sumDecimalNumbers([
    decimalOf(latest),
    negateDecimalNumber(decimalOf(previous)),
  ]);
}

export function prioritizeCurrency(
  amounts: CurrencyAmount[],
  preferredCurrency: string,
): CurrencyAmount[] {
  return [...amounts].sort((a, b) => {
    if (a.currency === preferredCurrency) return -1;
    if (b.currency === preferredCurrency) return 1;
    return a.currency.localeCompare(b.currency);
  });
}

export function getComparableAmount(
  amounts: CurrencyAmount[],
  preferredCurrency: string,
): CurrencyAmount | null {
  return (
    amounts.find((amount) => amount.currency === preferredCurrency) ??
    (amounts.length === 1 ? amounts[0] : null)
  );
}

/**
 * Every account in the hierarchy, parents and roots included. A node's own
 * `balance` holds just its direct postings (`balanceChildren` rolls up the
 * subtree), so reading each node's own balance counts every posting once.
 * Keeping only leaves dropped a parent's real holdings, and with them whole
 * Accounts rows and distribution slices (w4/1030). Callers drop zero rows.
 */
function flattenHierarchy(input?: unknown): HierarchyNode[] {
  if (!input) return [];
  const roots: HierarchyNode[] = Array.isArray(input)
    ? (input as HierarchyNode[])
    : [input as HierarchyNode];

  const nodes: HierarchyNode[] = [];
  const stack = [...roots];
  while (stack.length) {
    const node = stack.pop();
    if (!node) continue;
    nodes.push(node);
    if (Array.isArray(node.children)) stack.push(...node.children);
  }
  return nodes;
}

export function buildAccountBalanceRows({
  assets,
  liabilities,
  preferredCurrency,
  invertLiabilities,
}: {
  assets?: unknown;
  liabilities?: unknown;
  preferredCurrency: string;
  invertLiabilities: boolean;
}): AccountBalanceRow[] {
  const buildRows = (
    input: unknown,
    kind: AccountBalanceRow["kind"],
    inverted: boolean,
  ) =>
    flattenHierarchy(input)
      .map((node): AccountBalanceRow => {
        const amounts = prioritizeCurrency(
          getBalanceAmounts(node.balance, inverted),
          preferredCurrency,
        );
        const comparable = getComparableAmount(amounts, preferredCurrency);
        const fallbackMagnitude = Math.max(
          0,
          ...amounts.map((amount) => Math.abs(amount.value)),
        );
        return {
          account: node.account,
          label: node.account.split(":").pop() ?? node.account,
          kind,
          amounts,
          sortValue: Math.abs(comparable?.value ?? fallbackMagnitude),
        };
      })
      .filter((row) => row.amounts.some((amount) => amount.value !== 0));

  return [
    ...buildRows(assets, "asset", false),
    ...buildRows(liabilities, "liability", invertLiabilities),
  ].sort(
    (a, b) => b.sortValue - a.sortValue || a.account.localeCompare(b.account),
  );
}

export function getIntervalDates(
  income: IntervalDataSeries,
  expenses: IntervalDataSeries,
): string[] {
  return Array.from(
    new Set([
      ...income.map((point) => point.date),
      ...expenses.map((point) => point.date),
    ]),
  ).sort((a, b) => a.localeCompare(b));
}

export function getLatestActiveIntervalDate(
  income: IntervalDataSeries,
  expenses: IntervalDataSeries,
): string | undefined {
  return [...income, ...expenses].reduce<string | undefined>(
    (latest, point) =>
      pointHasActivity(point) && (!latest || point.date > latest)
        ? point.date
        : latest,
    undefined,
  );
}

/**
 * The month Money movement opens on when the Overview is filtered: the latest
 * month with activity that has already begun, else the latest begun month, so
 * a selection reaching past today (`time=year`) never opens on an empty future
 * month. A selection wholly in the future keeps its last month.
 */
export function getFilteredDefaultIntervalDate(
  income: IntervalDataSeries,
  expenses: IntervalDataSeries,
  now = new Date(),
): string | undefined {
  const today = toLocalISODate(now);
  const begun = (date: string) => `${date.slice(0, 8)}01` <= today;
  const dates = getIntervalDates(income, expenses);
  const latestActive = [...income, ...expenses]
    .filter((point) => begun(point.date) && pointHasActivity(point))
    .map((point) => point.date)
    .sort((a, b) => a.localeCompare(b))
    .at(-1);
  return latestActive ?? dates.filter(begun).at(-1) ?? dates.at(-1);
}

/**
 * Each unit's mean over the preceding points, from the ledger's decimals. It
 * is rounded once, two places past the finest source amount: a mean over two
 * points is then exact and one over three (10/3) stops there, while doubles
 * read 9007199254740993 as …992 and rounding to cents made a 0.003 BTC
 * average 0 (w4/1029). Display applies the unit's own digits afterwards.
 */
function averageBalances(
  points: IntervalDataSeries,
  inverted: boolean,
  preferredCurrency: string,
): CurrencyAmount[] {
  if (points.length === 0) return [];
  const totals = new Map<string, string[]>();
  for (const point of points) {
    for (const amount of getBalanceAmounts(point.balance, inverted)) {
      totals.set(amount.currency, [
        ...(totals.get(amount.currency) ?? []),
        decimalOf(amount),
      ]);
    }
  }
  return prioritizeCurrency(
    Array.from(totals, ([currency, amounts]) => {
      const exact = sumDecimalFractions(
        [
          {
            value: sumDecimalNumbers(amounts),
            numerator: 1,
            denominator: points.length,
          },
        ],
        Math.max(0, ...amounts.map(fractionDigitsOf)) + 2,
      );
      return { currency, value: Number(exact), exact };
    }),
    preferredCurrency,
  );
}

export function getMovementSnapshot({
  series,
  date,
  preferredCurrency,
  inverted,
}: {
  series: IntervalDataSeries;
  date: string;
  preferredCurrency: string;
  inverted: boolean;
}): MovementSnapshot {
  const point = series.find((candidate) => candidate.date === date);
  const previousPoints = series
    .filter((candidate) => candidate.date < date)
    .slice(-3);

  const categories = Object.entries(point?.accountBalances ?? {})
    .map(([account, rawBalance]): MovementCategory => {
      const balance =
        rawBalance && typeof rawBalance === "object"
          ? (rawBalance as Record<string, unknown>)
          : {};
      const amounts = prioritizeCurrency(
        getBalanceAmounts(balance, inverted),
        preferredCurrency,
      );
      const comparable = getComparableAmount(amounts, preferredCurrency);
      const fallbackMagnitude = Math.max(
        0,
        ...amounts.map((amount) => Math.abs(amount.value)),
      );
      return {
        account,
        label: account.split(":").pop() ?? account,
        amounts,
        sortValue: Math.abs(comparable?.value ?? fallbackMagnitude),
      };
    })
    .filter((category) => category.amounts.some((amount) => amount.value !== 0))
    .sort(
      (a, b) => b.sortValue - a.sortValue || a.account.localeCompare(b.account),
    );

  return {
    date,
    total: prioritizeCurrency(
      getBalanceAmounts(point?.balance, inverted),
      preferredCurrency,
    ),
    average: averageBalances(previousPoints, inverted, preferredCurrency),
    categories,
  };
}

/**
 * Formats a period date as a month-and-year label in the application language.
 *
 * `language` is required: with an undefined locale `Intl.DateTimeFormat` falls
 * back to the browser's preference, so a French app viewed in an en-US browser
 * renders "December 2016" next to French headings (and vice versa).
 */
export function formatPeriodMonth(date: string, language: string): string {
  return (
    formatLedgerDate(date, language, { month: "long", year: "numeric" }) ?? date
  );
}

export function isPartialMonthlyPeriod(
  date: string,
  now = new Date(),
): boolean {
  const period = new Date(`${date}T00:00:00`);
  if (Number.isNaN(period.getTime())) return false;
  return (
    period.getFullYear() === now.getFullYear() &&
    period.getMonth() === now.getMonth() &&
    now.getDate() < period.getDate()
  );
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

export function toLocalISODate(value: Date): string {
  return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}`;
}

/**
 * Convert an overview monthly interval endpoint (`YYYY-MM-DD`, usually month
 * end) into a Fava time filter. Complete months become `YYYY-MM`; the current
 * unfinished month becomes an inclusive date range ending today so drill-downs
 * do not widen past the card's actual data.
 */
export function intervalDateToTimeFilter(
  date: string,
  now = new Date(),
): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return date;

  const [, year, month] = match;
  const monthKey = `${year}-${month}`;

  if (!isPartialMonthlyPeriod(date, now)) {
    return monthKey;
  }

  const monthStart = `${year}-${month}-01`;
  const today = toLocalISODate(now);
  const end = today < date ? today : date;
  return `${monthStart} - ${end}`;
}

/** Last day of the month containing an inclusive `YYYY-MM-DD` date. */
function monthEndISODate(date: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return date;
  const [, year, month] = match;
  return toLocalISODate(new Date(Number(year), Number(month), 0));
}

/**
 * Narrow the drill-down period to the intersection of the selected month and
 * the existing global time filter, so a selection spanning partial months (for
 * example `2026-01-15 - 2026-02-15`) is never widened to a whole month. Falls
 * back to the derived month only when the ranges are disjoint or unparseable.
 */
export function resolveMovementTimeFilter(
  selectedDate: string,
  existingTime: string | undefined,
  now = new Date(),
): string {
  const derived = intervalDateToTimeFilter(selectedDate, now);
  const existing = existingTime?.trim();
  if (!existing) return derived;

  const existingRange = parseConcreteTimeFilter(existing);
  const derivedRange = parseConcreteTimeFilter(derived);
  if (!existingRange || !derivedRange) return derived;

  const startDate =
    existingRange.startDate > derivedRange.startDate
      ? existingRange.startDate
      : derivedRange.startDate;
  const endDate =
    existingRange.endDate < derivedRange.endDate
      ? existingRange.endDate
      : derivedRange.endDate;
  if (startDate > endDate) return derived;

  // Collapse to the bare month key when the intersection is the whole month.
  if (
    /^(\d{4})-(\d{2})-01$/.test(startDate) &&
    endDate === monthEndISODate(startDate) &&
    startDate.slice(0, 7) === endDate.slice(0, 7)
  ) {
    return startDate.slice(0, 7);
  }

  return `${startDate} - ${endDate}`;
}

export type DistributionItem = {
  name: string;
  label: string;
  /** The amount as a double, for geometry, ordering and shares. */
  value: number;
  /** The ledger's decimal, for text: a double drops its digits (w4/m53). */
  exact: string;
};

export type DistributionData = {
  items: DistributionItem[];
  /** The one unit every `value` is denominated in, or null when there is none. */
  unit: string | null;
  /** Every unit the underlying accounts hold, so scope can be disclosed. */
  units: string[];
};

/** Distribution slices for one unit; `units` reports what was left out. */
export function buildDistributionData(
  input?: unknown,
  inverse?: boolean,
  /** The ledger's operating currency, preferred when the accounts hold it. */
  preferredCurrency?: string | null,
): DistributionData {
  if (!input) return { items: [], unit: null, units: [] };

  const leaves = flattenHierarchy(input).map((node) => ({
    name: node.account,
    label: (node.account.split(":")?.pop() ?? "Unnamed") as string,
    amounts: balanceToAmounts(node.balance, inverse),
  }));

  const allAmounts = leaves.map((leaf) => leaf.amounts);
  const units = collectUnits(allAmounts);
  const unit = chooseDisplayUnit(allAmounts, preferredCurrency);
  if (!unit) return { items: [], unit: null, units };

  const items = leaves.flatMap((leaf) => {
    const exact = leaf.amounts.get(unit);
    const value = Number(exact);
    return exact !== undefined && Number.isFinite(value) && value !== 0
      ? [{ name: leaf.name, label: leaf.label, value, exact }]
      : [];
  });

  return { items, unit, units };
}
